"use client";

import { useState } from "react";
import type { CandidateCertification } from "@/lib/api/types";
import { formatYearMonthShort } from "@/lib/date/date";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { CertificateLogo } from "@/features/candidate";
import { SectionCard } from "../section-layout";
import { Icon } from "@/components/ui/icon";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";

export function CertificationsSection({
  certifications,
}: {
  certifications: CandidateCertification[];
}) {
  const [showAll, setShowAll] = useState(false);
  if (certifications.length === 0) return null;

  const visible = showAll ? certifications : certifications.slice(0, 2);

  return (
    <SectionCard title="Licenses & Certifications">
      {visible.map((cert, idx) => (
        <div key={idx} className="w-full min-w-0">
          {idx > 0 && <div className="h-px w-full bg-border" />}
          <div
            className={`flex w-full min-w-0 shrink-0 flex-col items-stretch border-x border-border bg-surface-elevated p-5 ${idx === 0 ? "border-t border-border" : ""} ${idx === visible.length - 1 && certifications.length <= 2 ? "rounded-b-md border-b border-border" : ""}`}
          >
            <div className="flex w-full min-w-0 gap-2.5 items-start">
              <CertificateLogo
                size="lg"
                domain={null}
                name={cert.organization ?? cert.name}
              />
              <div className="flex min-w-0 flex-[1_0_0] flex-col gap-1 items-start justify-center">
                <p className="w-full text-sm font-medium tracking-[-0.14px] text-foreground">
                  <HighlightedText source="certifications">{titleCasePreserveAcronyms(cert.name)}</HighlightedText>
                </p>
                {(cert.organization || cert.certificate_number) && (
                  <p className="w-full text-[13px] font-normal tracking-[-0.13px] text-foreground/70">
                    {cert.organization && titleCasePreserveAcronyms(cert.organization)}
                    {cert.organization && cert.certificate_number && (
                      <span className="text-foreground/25"> · </span>
                    )}
                    {cert.certificate_number && `#${cert.certificate_number}`}
                  </p>
                )}
                {(cert.start_date || cert.end_date) && (
                  <p className="w-full text-[13px] font-normal tracking-[-0.13px] text-foreground/70">
                    {cert.start_date &&
                      `Issued ${formatYearMonthShort(cert.start_date)}`}
                    {cert.start_date && cert.end_date && (
                      <span className="text-foreground/25"> · </span>
                    )}
                    {cert.end_date &&
                      `Expires ${formatYearMonthShort(cert.end_date)}`}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      {certifications.length > 2 && (
        <>
          <div className="h-px w-full bg-border" />
          <div className="flex w-full shrink-0 flex-col items-start gap-4 rounded-b-md border-x border-b border-border bg-surface-elevated p-5">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="flex min-h-9 w-full items-center justify-start gap-0.5 overflow-clip rounded-md text-left cursor-pointer sm:min-h-0 sm:w-auto sm:justify-center"
          >
            <span className="min-w-0 break-words text-sm font-medium text-foreground/50 leading-5 px-0.5">
              {showAll
                ? "Show fewer"
                : `Show all ${certifications.length} licenses & certifications`}
            </span>
            <Icon
              name={showAll ? "IconChevronTop" : "IconChevronDownSmall"}
              size={16}
              className="shrink-0 text-foreground/50"
            />
          </button>
          </div>
        </>
      )}
    </SectionCard>
  );
}
